import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';

export type TypeMouvement =
  | 'part_vente'
  | 'reprise_avoir'
  | 'frais'
  | 'avance'
  | 'remboursement_avance'
  | 'reversement'
  | 'ajustement';
export type NatureAvance = 'fonderie' | 'tirage' | 'encadrement' | 'transport' | 'autre';
export type ResultatEnvoi = 'envoye' | 'capture' | 'echec';
export interface CorpsConditions {
  artisteId: string;
  effetLe: string;
  mode: 'prix_artiste' | 'commission';
  tauxCommissionPdb?: number;
  faitGenerateur: 'encaissement' | 'emission';
  periodicite: 'mensuelle' | 'trimestrielle' | 'a_la_demande';
  fraisParDefaut: string;
  contactId: string | null;
}
export interface ConditionsArtiste extends CorpsConditions {
  id: string;
}
export interface MouvementArtiste {
  id: string;
  artisteId: string;
  type: TypeMouvement;
  montantCentimes: number;
  devise: string;
  date: string;
  cle: string;
  source?: {
    factureId?: string;
    rang?: number;
    reglementId?: string;
    avoirId?: string;
    avanceId?: string;
    oeuvreId?: string;
  };
  base?: {
    mode: 'prix_artiste' | 'commission';
    htCentimes?: number;
    tauxCommissionPdb?: number;
    prixArtisteCentimes?: number;
    quantite: number;
    fraction?: { numerateur: number; denominateur: number };
  };
  motif?: string;
  moyen?: string;
  reference?: string;
  releveId: string | null;
}
export interface TotauxReleve {
  parType: Partial<Record<TypeMouvement, number>>;
  soldePeriodeCentimes: number;
  soldeFinCentimes: number;
  imputationsCentimes: number;
  soldeAVerserCentimes: number;
}
export interface ReleveArtiste {
  id: string;
  artisteId: string;
  debut: string;
  fin: string;
  etat: 'brouillon' | 'valide';
  numero: string | null;
  valideLe: string | null;
  validePar: string | null;
  version: number;
  langue: 'fr' | 'en';
  soldeReporteCentimes: number;
  totaux?: TotauxReleve;
  piece?: unknown;
  pdfCle: string | null;
  envois: { le: string; destinataire: string; par: string | null; resultat: ResultatEnvoi }[];
}
export interface EcritureFigee {
  id: string;
  date: string;
  libelle: string;
  piece?: string;
  montantCentimes: number;
}
export interface AvanceFigee {
  id: string;
  date: string;
  nature: string;
  montantCentimes: number;
  restantDuCentimes: number;
  imputationCentimes: number;
}
export interface ReleveDetail extends ReleveArtiste {
  totaux: TotauxReleve;
  ecritures: (MouvementArtiste | EcritureFigee)[];
  avances: (AvanceArtiste | AvanceFigee)[];
  imputations: { avanceId: string; montantCentimes: number }[];
}
export interface CorpsAvance {
  artisteId: string;
  nature: NatureAvance;
  montantCentimes?: number;
  date: string;
  regle: 'sur_ventes' | 'hors_ventes';
  plafondPdb?: number;
  oeuvreId?: string;
  editionId?: string;
  fournisseurId?: string;
  piece?: { type: 'media' | 'document'; id: string };
  coutProductionId?: string;
}
export interface AvanceArtiste extends Omit<
  CorpsAvance,
  'oeuvreId' | 'editionId' | 'fournisseurId' | 'piece' | 'coutProductionId' | 'montantCentimes'
> {
  id: string;
  montantCentimes: number;
  oeuvreId: string | null;
  editionId: string | null;
  fournisseurId: string | null;
  coutProductionId: string | null;
  piece: NonNullable<CorpsAvance['piece']> | null;
  restantDuCentimes: number;
  imputations: { releveId: string; montantCentimes: number; le: string }[];
  remboursements: { mouvementId: string; montantCentimes: number; le: string }[];
}
export interface TableauComptesArtistes {
  soldes: { artisteId: string; nom: string; soldeCentimes: number }[];
  compteurs: { brouillonsAValider: number; validesAEnvoyer: number; aReverser: number };
  avances: Pick<AvanceArtiste, 'id' | 'artisteId' | 'nature' | 'restantDuCentimes' | 'date'>[];
  blocages: {
    factureId: string;
    rang: number;
    artisteId: string;
    cause: 'conditions_absentes' | 'prix_artiste_absent';
  }[];
}
export interface CompteArtiste {
  artisteId: string;
  soldeCentimes: number;
  mouvements: MouvementArtiste[];
  releves: Pick<ReleveArtiste, 'id' | 'debut' | 'fin' | 'etat' | 'numero' | 'totaux'>[];
  avances: Pick<
    AvanceArtiste,
    'id' | 'nature' | 'date' | 'montantCentimes' | 'restantDuCentimes'
  >[];
  conditions: ConditionsArtiste | null;
  commission: string | null;
}
export interface CorpsEcriture {
  artisteId: string;
  montantCentimes: number;
  date: string;
  cle: string;
}
export interface CorpsFrais extends CorpsEcriture {
  factureId?: string;
  libelle: string;
}
export interface CorpsAjustement extends CorpsEcriture {
  motif: string;
}
export interface CorpsReversement extends CorpsEcriture {
  moyen: string;
  reference?: string;
  confirmerDepassement?: boolean;
  motif?: string;
}
export interface PeriodeReleve {
  debut: string;
  fin: string;
}
export interface FiltresMouvements {
  artisteId?: string;
  factureId?: string;
  du?: string;
  au?: string;
  type?: TypeMouvement;
  page?: number;
  parPage?: number;
}
const racine = (g: string) => `/g/${encodeURIComponent(g)}`;
const comptes = (g: string) => `${racine(g)}/comptes-artistes`;
function parametres(filtres: object): string {
  const p = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres))
    if (valeur !== undefined) p.set(cle, String(valeur));
  return p.toString();
}
export const comptesArtistesApi = {
  tableau: (g: string, a?: AppelApi) =>
    api.get<TableauComptesArtistes>(comptes(g), { galerie: g, ...a }),
  conditions: (g: string, artisteId: string, a?: AppelApi) =>
    api.get<{ conditions: ConditionsArtiste[] }>(
      `${comptes(g)}/conditions?${parametres({ artisteId })}`,
      { galerie: g, ...a },
    ),
  creerConditions: (g: string, corps: CorpsConditions, a?: AppelApi) =>
    api.post<{ conditions: ConditionsArtiste }>(`${comptes(g)}/conditions`, corps, {
      galerie: g,
      ...a,
    }),
  modifierConditions: (
    g: string,
    id: string,
    corps: Partial<Omit<CorpsConditions, 'artisteId'>>,
    a?: AppelApi,
  ) =>
    api.patch<{ conditions: ConditionsArtiste }>(
      `${comptes(g)}/conditions/${encodeURIComponent(id)}`,
      corps,
      { galerie: g, ...a },
    ),
  mouvements: (g: string, f: FiltresMouvements = {}, a?: AppelApi) =>
    api.get<{ mouvements: MouvementArtiste[]; pagination: Pagination }>(
      `${comptes(g)}/mouvements?${parametres(f)}`,
      { galerie: g, ...a },
    ),
  frais: (g: string, corps: CorpsFrais, a?: AppelApi) =>
    api.post<{ mouvement: MouvementArtiste }>(`${comptes(g)}/frais`, corps, { galerie: g, ...a }),
  ajustement: (g: string, corps: CorpsAjustement, a?: AppelApi) =>
    api.post<{ mouvement: MouvementArtiste }>(`${comptes(g)}/ajustements`, corps, {
      galerie: g,
      ...a,
    }),
  rapprocher: (g: string, corps: { factureId?: string }, a?: AppelApi) =>
    api.post<{ travailId: string }>(`${comptes(g)}/rapprocher`, corps, { galerie: g, ...a }),
  exporter: (g: string, f: { du?: string; au?: string }, a?: AppelApi) =>
    requeteTelechargement(`${comptes(g)}/export.csv?${parametres(f)}`, { galerie: g, ...a }),
  releves: (
    g: string,
    f: { artisteId?: string; etat?: ReleveArtiste['etat'] } = {},
    a?: AppelApi,
  ) =>
    api.get<{ releves: ReleveDetail[] }>(`${comptes(g)}/releves?${parametres(f)}`, {
      galerie: g,
      ...a,
    }),
  releve: (g: string, id: string, a?: AppelApi) =>
    api.get<{ releve: ReleveDetail }>(`${comptes(g)}/releves/${encodeURIComponent(id)}`, {
      galerie: g,
      ...a,
    }),
  creerReleve: (g: string, corps: PeriodeReleve & { artisteId: string }, a?: AppelApi) =>
    api.post<{ releve: ReleveDetail }>(`${comptes(g)}/releves`, corps, { galerie: g, ...a }),
  lotReleves: (g: string, corps: PeriodeReleve, a?: AppelApi) =>
    api.post<{ releves: ReleveArtiste[] }>(`${comptes(g)}/releves/lot`, corps, {
      galerie: g,
      ...a,
    }),
  supprimerReleve: (g: string, id: string, a?: AppelApi) =>
    api.delete<void>(`${comptes(g)}/releves/${encodeURIComponent(id)}`, { galerie: g, ...a }),
  validerReleve: (g: string, id: string, versionAttendue: number, a?: AppelApi) =>
    api.post<{ releve: ReleveArtiste }>(
      `${comptes(g)}/releves/${encodeURIComponent(id)}/valider`,
      { versionAttendue },
      { galerie: g, ...a },
    ),
  pdfReleve: (g: string, id: string, a?: AppelApi) =>
    api.get<{ url: string }>(`${comptes(g)}/releves/${encodeURIComponent(id)}/pdf`, {
      galerie: g,
      ...a,
    }),
  envoyerReleve: (g: string, id: string, a?: AppelApi) =>
    api.post<{ releve: ReleveArtiste; envoi: { resultat: ResultatEnvoi } }>(
      `${comptes(g)}/releves/${encodeURIComponent(id)}/envoyer`,
      {},
      { galerie: g, ...a },
    ),
  reversement: (g: string, corps: CorpsReversement, a?: AppelApi) =>
    api.post<{ mouvements: MouvementArtiste[] }>(`${comptes(g)}/reversements`, corps, {
      galerie: g,
      ...a,
    }),
  avances: (g: string, f: { artisteId?: string; enCours?: 'true' | 'false' } = {}, a?: AppelApi) =>
    api.get<{ avances: AvanceArtiste[] }>(`${racine(g)}/avances?${parametres(f)}`, {
      galerie: g,
      ...a,
    }),
  avance: (g: string, id: string, a?: AppelApi) =>
    api.get<{ avance: AvanceArtiste }>(`${racine(g)}/avances/${encodeURIComponent(id)}`, {
      galerie: g,
      ...a,
    }),
  creerAvance: (g: string, corps: CorpsAvance, a?: AppelApi) =>
    api.post<{ avance: AvanceArtiste }>(`${racine(g)}/avances`, corps, { galerie: g, ...a }),
  rembourserAvance: (
    g: string,
    id: string,
    corps: Omit<CorpsEcriture, 'artisteId'>,
    a?: AppelApi,
  ) =>
    api.post<{ avance: AvanceArtiste; mouvement: MouvementArtiste }>(
      `${racine(g)}/avances/${encodeURIComponent(id)}/remboursements`,
      corps,
      { galerie: g, ...a },
    ),
  compte: (g: string, id: string, a?: AppelApi) =>
    api.get<CompteArtiste>(`${racine(g)}/artistes/${encodeURIComponent(id)}/compte`, {
      galerie: g,
      ...a,
    }),
};

export function useTableauComptesArtistes(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'tableau'),
    queryFn: (c) => comptesArtistesApi.tableau(g, appelDeRequete(c)),
  });
}

export function useConditionsArtiste(g: string, artisteId: string) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'conditions', artisteId),
    queryFn: (c) => comptesArtistesApi.conditions(g, artisteId, appelDeRequete(c)),
  });
}

export function useMouvementsArtistes(g: string, filtres: FiltresMouvements = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'mouvements', filtres),
    queryFn: (c) => comptesArtistesApi.mouvements(g, filtres, appelDeRequete(c)),
  });
}

export function useRelevesArtistes(
  g: string,
  filtres: { artisteId?: string; etat?: ReleveArtiste['etat'] } = {},
) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'releves', filtres),
    queryFn: (c) => comptesArtistesApi.releves(g, filtres, appelDeRequete(c)),
  });
}

export function useReleveArtiste(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'releve', id),
    queryFn: (c) => comptesArtistesApi.releve(g, id, appelDeRequete(c)),
  });
}

export function useAvancesArtistes(
  g: string,
  filtres: { artisteId?: string; enCours?: 'true' | 'false' } = {},
) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'avances', filtres),
    queryFn: (c) => comptesArtistesApi.avances(g, filtres, appelDeRequete(c)),
  });
}

export function useAvanceArtiste(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'avance', id),
    queryFn: (c) => comptesArtistesApi.avance(g, id, appelDeRequete(c)),
  });
}

export function useCompteArtiste(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'comptes-artistes', 'compte', id),
    queryFn: (c) => comptesArtistesApi.compte(g, id, appelDeRequete(c)),
  });
}

export function useCreerConditionsArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsConditions, a) => comptesArtistesApi.creerConditions(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useModifierConditionsArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; corps: Partial<Omit<CorpsConditions, 'artisteId'>> }, a) =>
        comptesArtistesApi.modifierConditions(g, v.id, v.corps, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useSaisirFraisArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsFrais, a) => comptesArtistesApi.frais(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useSaisirAjustementArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsAjustement, a) => comptesArtistesApi.ajustement(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useRapprocherComptesArtistes(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { factureId?: string }, a) => comptesArtistesApi.rapprocher(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useCreerReleveArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: PeriodeReleve & { artisteId: string }, a) =>
        comptesArtistesApi.creerReleve(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function usePreparerRelevesArtistes(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: PeriodeReleve, a) => comptesArtistesApi.lotReleves(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useSupprimerReleveArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: string, a) => comptesArtistesApi.supprimerReleve(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useValiderReleveArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; versionAttendue: number }, a) =>
        comptesArtistesApi.validerReleve(g, v.id, v.versionAttendue, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useEnvoyerReleveArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: string, a) => comptesArtistesApi.envoyerReleve(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useSaisirReversementArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsReversement, a) => comptesArtistesApi.reversement(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useCreerAvanceArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsAvance, a) => comptesArtistesApi.creerAvance(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}

export function useRembourserAvanceArtiste(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; corps: Omit<CorpsEcriture, 'artisteId'> }, a) =>
        comptesArtistesApi.rembourserAvance(g, v.id, v.corps, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'comptes-artistes') });
      },
    }),
  );
}
